import * as React from "react";

interface ClientLockContextValue {
  /** The client's configuration is flagged as locked, regardless of who is looking. Drives the warnings. */
  isConfigLocked: boolean;
  /** The current user must be prevented from making changes: locked and not a Super Admin. Drives `disabled`. */
  isReadOnly: boolean;
}

/**
 * Defaults to an unlocked account so that shared components (for example DataTable) can read this
 * context on pages that never render a provider.
 */
const ClientLockContext = React.createContext<ClientLockContextValue>({
  isConfigLocked: false,
  isReadOnly: false
});

interface Props {
  isConfigLocked: boolean;
  isReadOnly: boolean;
  children: React.ReactNode;
}

const ClientLockProvider: React.FC<Props> = ({ isConfigLocked, isReadOnly, children }) => {
  const value = React.useMemo(() => ({ isConfigLocked, isReadOnly }), [isConfigLocked, isReadOnly]);
  return <ClientLockContext.Provider value={value}>{children}</ClientLockContext.Provider>;
};

const useClientLock = (): ClientLockContextValue => React.useContext(ClientLockContext);

const useClientReadOnly = (): boolean => React.useContext(ClientLockContext).isReadOnly;

export { ClientLockProvider, useClientLock, useClientReadOnly };
